import { z } from 'zod';

import { INCIDENT_PHOTO_CAP } from './ticket-schemas.js';

// Events module (§6.7) S4 — Flash-mode batch check-in (R4.2/R4.3/R4.4).
// BACKEND-ONLY seam: the PWA/operator UI consuming these schemas is deferred
// to S4-UI. eventId/locationId/photoPolicy are resolved server-side from the
// Event record (design ADR-E2 seam) — NEVER accepted from the client.
//
// event-photo-capture Slice C — incidentPhotoKeys mirrors CreateTicketRequestSchema's
// field 1:1 (single-sourced cap via INCIDENT_PHOTO_CAP), independent of photoPolicy.

const E164 = /^\+[1-9]\d{6,14}$/;

export const BatchCheckInItemSchema = z.object({
  plate: z.string().trim().min(1).max(20),
  ticketType: z.enum(['sms', 'qr']),
  qrTag: z.string().trim().min(1).max(64).optional(),
  phone: z.string().regex(E164).optional(),
  firstName: z.string().trim().max(60).optional(),
  lastName: z.string().trim().max(60).optional(),
  /** QA-oct I9 - optional; required when the location's check-in config marks parking_zone required. */
  parkingZone: z.string().trim().max(40).optional(),
  force: z.boolean().optional(),
  /**
   * Presigned-PUT storage keys (JQ4-22 flow), REQUIRED only when the event's
   * photoPolicy is 'required_4'; ignored under 'deferred'; 0-4 accepted
   * under 'optional' (R4.3). Enforcement of the count lives in the use case,
   * not the schema, since it depends on the event's photoPolicy.
   */
  photoKeys: z.array(z.string().trim().min(1)).max(4).optional(),
  /**
   * Optional incident (pre-existing damage) photo keys — 0..20, each non-empty.
   * Fully independent of photoKeys/photoPolicy above (event-photo-capture
   * Slice C, ticket-incident-photos parity). Enforcement of persistence lives
   * in the use case, mirroring CreateTicketUseCase's single-ticket path.
   */
  incidentPhotoKeys: z.array(z.string().trim().min(1)).max(INCIDENT_PHOTO_CAP).optional(),
});
export type BatchCheckInItem = z.infer<typeof BatchCheckInItemSchema>;

export const BatchCheckInRequestSchema = z.object({
  items: z.array(BatchCheckInItemSchema).min(1).max(100),
});
export type BatchCheckInRequest = z.infer<typeof BatchCheckInRequestSchema>;

export const BatchCheckInItemErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  /** Check-in field id (`parking_zone`) for `required_field_missing`, so the UI can name it. */
  field: z.string().optional(),
});
export type BatchCheckInItemError = z.infer<typeof BatchCheckInItemErrorSchema>;

export const BatchCheckInItemResultSchema = z.object({
  index: z.number().int().nonnegative(),
  plate: z.string(),
  success: z.boolean(),
  ticketId: z.string().optional(),
  ticketNumber: z.number().int().positive().optional(),
  photosPending: z.boolean().optional(),
  /**
   * flash-batch-sms-pay-clarity — per-item SMS dispatch outcome. Present only
   * on success results. 'sent' = provider dispatched successfully; 'failed' =
   * dispatch attempted but did not succeed (supervisors already notified);
   * 'skipped' = ticketType !== 'sms' or no phone (not an error).
   */
  smsStatus: z.enum(['sent', 'failed', 'skipped']).optional(),
  error: BatchCheckInItemErrorSchema.optional(),
});
export type BatchCheckInItemResult = z.infer<typeof BatchCheckInItemResultSchema>;

export const BatchCheckInResponseSchema = z.object({
  eventId: z.string(),
  results: z.array(BatchCheckInItemResultSchema),
});
export type BatchCheckInResponse = z.infer<typeof BatchCheckInResponseSchema>;
